import { useState } from "react";

/* ── Simulated knowledge base chunks ── */
interface KBChunk {
  id: string;
  text: string;
  expert_name: string;
  expert_title: string;
  variable_tags: string[];
  chunk_type: "belief" | "threshold" | "mechanism" | "boundary" | "synthesis";
  certainty: number;
  embedding_cluster: number; // 0-4, for visual grouping
  timestamp: string;
  session_id: string;
}

const DEMO_CHUNKS: KBChunk[] = [
  {
    id: "ch_001", text: "In my 20 years of family law, parental involvement is the single strongest predictor of favorable custody outcomes. Cases where both parents demonstrate active engagement show 3x better child adjustment scores.",
    expert_name: "Sarah Hartwell", expert_title: "Partner, Family Law Division",
    variable_tags: ["Parental_Involvement"], chunk_type: "belief", certainty: 0.82, embedding_cluster: 0,
    timestamp: "2026-03-15 10:23", session_id: "sess_hartwell_01",
  },
  {
    id: "ch_002", text: "The threshold is around 8 hours per week of direct engagement. Below that, the court sees it as performative. Above 15 hours, diminishing returns — quality matters more than quantity.",
    expert_name: "Sarah Hartwell", expert_title: "Partner, Family Law Division",
    variable_tags: ["Parental_Involvement"], chunk_type: "threshold", certainty: 0.75, embedding_cluster: 0,
    timestamp: "2026-03-15 10:25", session_id: "sess_hartwell_01",
  },
  {
    id: "ch_003", text: "Any documented domestic violence history shifts the calculus entirely. It's nearly deterministic — in LA County, DV findings result in supervised visitation 92% of the time.",
    expert_name: "Dr. Diana Washington", expert_title: "Chief Social Worker, LA County DCFS",
    variable_tags: ["Domestic_Violence_Risk"], chunk_type: "belief", certainty: 0.91, embedding_cluster: 1,
    timestamp: "2026-03-15 14:07", session_id: "sess_washington_01",
  },
  {
    id: "ch_004", text: "DV operates through a mandatory safety evaluation pathway. Once triggered, the burden of proof shifts to the accused parent. This is the mechanism — it's procedural, not just correlational.",
    expert_name: "Dr. Diana Washington", expert_title: "Chief Social Worker, LA County DCFS",
    variable_tags: ["Domestic_Violence_Risk"], chunk_type: "mechanism", certainty: 0.88, embedding_cluster: 1,
    timestamp: "2026-03-15 14:12", session_id: "sess_washington_01",
  },
  {
    id: "ch_005", text: "The DV effect breaks down in cases with mutual restraining orders or where allegations are unsubstantiated. Roughly 15-20% of DV claims in custody disputes are strategic rather than genuine safety concerns.",
    expert_name: "Hon. Rebecca Paulson", expert_title: "Former Family Court Judge",
    variable_tags: ["Domestic_Violence_Risk"], chunk_type: "boundary", certainty: 0.70, embedding_cluster: 1,
    timestamp: "2026-03-16 09:31", session_id: "sess_paulson_01",
  },
  {
    id: "ch_006", text: "Attachment security measured via the Strange Situation protocol predicts long-term adjustment. Securely attached children show 2.5x better outcomes in disrupted family structures.",
    expert_name: "Dr. Michael Kwan", expert_title: "Director, Child & Family Assessment Center, UCLA",
    variable_tags: ["Child_Attachment_Quality"], chunk_type: "belief", certainty: 0.74, embedding_cluster: 2,
    timestamp: "2026-03-16 11:15", session_id: "sess_kwan_01",
  },
  {
    id: "ch_007", text: "The attachment effect is heavily confounded with socioeconomic status. Parents with more resources can provide more consistent environments. The isolated causal effect is probably 40-60% of the raw correlation.",
    expert_name: "Dr. Michael Kwan", expert_title: "Director, Child & Family Assessment Center, UCLA",
    variable_tags: ["Child_Attachment_Quality"], chunk_type: "boundary", certainty: 0.65, embedding_cluster: 2,
    timestamp: "2026-03-16 11:22", session_id: "sess_kwan_01",
  },
  {
    id: "ch_008", text: "When income disparity exceeds 3:1 between parents, the lower-earning spouse faces systematic disadvantage in litigation quality. Legal representation asymmetry is the primary mechanism.",
    expert_name: "James Thornton", expert_title: "Managing Director, Forensic Accounting, Deloitte",
    variable_tags: ["Income_Disparity_Index"], chunk_type: "mechanism", certainty: 0.68, embedding_cluster: 3,
    timestamp: "2026-03-14 15:40", session_id: "sess_thornton_01",
  },
  {
    id: "ch_009", text: "Active substance abuse is disqualifying. But recovery history of 2+ years is viewed favorably by courts — it shows insight and commitment. The key threshold is documented sobriety duration.",
    expert_name: "Dr. Adriana Morales", expert_title: "Professor of Family Law, Stanford",
    variable_tags: ["Substance_Abuse_History"], chunk_type: "threshold", certainty: 0.88, embedding_cluster: 4,
    timestamp: "2026-03-13 16:55", session_id: "sess_morales_01",
  },
  {
    id: "ch_010", text: "Judges weight residential stability heavily. A parent who's moved 3 times in 2 years raises red flags about environment consistency for schooling and social development.",
    expert_name: "Hon. Rebecca Paulson", expert_title: "Former Family Court Judge",
    variable_tags: ["Geographic_Stability"], chunk_type: "belief", certainty: 0.60, embedding_cluster: 4,
    timestamp: "2026-03-17 09:10", session_id: "sess_paulson_01",
  },
  {
    id: "ch_011", text: "Across all six variables, the interaction effects matter enormously. Parental involvement has 3x the effect when combined with low conflict. High conflict moderates almost everything downward.",
    expert_name: "Sarah Hartwell", expert_title: "Partner, Family Law Division",
    variable_tags: ["Parental_Involvement", "Domestic_Violence_Risk"], chunk_type: "synthesis", certainty: 0.78, embedding_cluster: 0,
    timestamp: "2026-03-15 10:45", session_id: "sess_hartwell_01",
  },
  {
    id: "ch_012", text: "The top three predictors by effect size in my experience: 1) Domestic violence history (0.6-0.7), 2) Parental involvement quality (0.4-0.5), 3) Child attachment security (0.3-0.4). Everything else is secondary.",
    expert_name: "Dr. Diana Washington", expert_title: "Chief Social Worker, LA County DCFS",
    variable_tags: ["Domestic_Violence_Risk", "Parental_Involvement", "Child_Attachment_Quality"], chunk_type: "synthesis", certainty: 0.85, embedding_cluster: 1,
    timestamp: "2026-03-15 14:30", session_id: "sess_washington_01",
  },
];

const CHUNK_TYPE_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  belief:    { label: "Belief",    color: "#3b82f6", icon: "B" },
  threshold: { label: "Threshold", color: "#f59e0b", icon: "T" },
  mechanism: { label: "Mechanism", color: "#8b5cf6", icon: "M" },
  boundary:  { label: "Boundary",  color: "#ef4444", icon: "X" },
  synthesis: { label: "Synthesis", color: "#10b981", icon: "S" },
};

const CLUSTER_COLORS = ["#6366f1", "#10b981", "#0891b2", "#f59e0b", "#ef4444"];
const CLUSTER_LABELS = ["Parental Involvement", "Domestic Violence", "Child Attachment", "Financial Factors", "Stability & Substance"];

/* ── Embedding scatter (simulated) ── */
function EmbeddingMap({ chunks, searchHighlight }: { chunks: KBChunk[]; searchHighlight: Set<string> }) {
  // Simulated 2D positions for each cluster
  const clusterCenters = [
    { x: 80, y: 60 }, { x: 280, y: 45 }, { x: 180, y: 150 },
    { x: 380, y: 120 }, { x: 320, y: 170 },
  ];

  const rng = (seed: number) => {
    let s = seed;
    return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
  };

  return (
    <div style={{
      padding: "12px 16px", borderRadius: 10, background: "rgba(0,0,0,0.02)",
      border: "1px solid rgba(0,0,0,0.06)",
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.35)", marginBottom: 8 }}>
        Embedding Space (t-SNE projection)
      </div>
      <svg width="100%" viewBox="0 0 460 200" style={{ maxHeight: 180 }}>
        {/* Cluster labels */}
        {clusterCenters.map((c, i) => (
          <text key={i} x={c.x} y={c.y - 22} textAnchor="middle" fontSize="8" fill={CLUSTER_COLORS[i]} fontWeight="600" opacity={0.6}>
            {CLUSTER_LABELS[i]}
          </text>
        ))}

        {/* Dots */}
        {chunks.map((chunk, idx) => {
          const center = clusterCenters[chunk.embedding_cluster];
          const rand = rng(idx * 7 + 13);
          const x = center.x + (rand() - 0.5) * 70;
          const y = center.y + (rand() - 0.5) * 50;
          const isHighlighted = searchHighlight.has(chunk.id);

          return (
            <g key={chunk.id}>
              {isHighlighted && (
                <circle cx={x} cy={y} r={10} fill={CLUSTER_COLORS[chunk.embedding_cluster]} opacity={0.15} />
              )}
              <circle
                cx={x} cy={y}
                r={isHighlighted ? 5 : 3.5}
                fill={CLUSTER_COLORS[chunk.embedding_cluster]}
                opacity={isHighlighted ? 1 : 0.5}
                stroke={isHighlighted ? "#000" : "none"}
                strokeWidth={isHighlighted ? 1.5 : 0}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Ingestion animation ── */
function IngestionFlow() {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
      borderRadius: 8, background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.1)",
      marginBottom: 12,
    }}>
      <div style={{ display: "flex", gap: 4 }}>
        {["Interview", "Chunk", "Embed", "Store"].map((step, i) => (
          <div key={step} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{
              width: 22, height: 22, borderRadius: 6, fontSize: 8, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "#6366f1", color: "#fff",
            }}>{i + 1}</div>
            <span style={{ fontSize: 10, fontWeight: 600, color: "#6366f1" }}>{step}</span>
            {i < 3 && (
              <svg width="12" height="8" viewBox="0 0 12 8" style={{ margin: "0 2px" }}>
                <line x1="0" y1="4" x2="8" y2="4" stroke="#6366f1" strokeWidth="1.5" opacity={0.4} />
                <polygon points="8,1.5 12,4 8,6.5" fill="#6366f1" opacity={0.4} />
              </svg>
            )}
          </div>
        ))}
      </div>
      <div style={{ marginLeft: "auto", fontSize: 10, color: "rgba(0,0,0,0.4)" }}>
        <span style={{ fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{DEMO_CHUNKS.length}</span> chunks stored
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function KnowledgeBase() {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<string | null>(null);
  const [filterCluster, setFilterCluster] = useState<number | null>(null);

  // Simple search: match against text, variable tags, expert name
  const searchLower = searchQuery.toLowerCase();
  const filtered = DEMO_CHUNKS.filter(chunk => {
    if (filterType && chunk.chunk_type !== filterType) return false;
    if (filterCluster !== null && chunk.embedding_cluster !== filterCluster) return false;
    if (searchQuery && !chunk.text.toLowerCase().includes(searchLower)
        && !chunk.variable_tags.some(t => t.toLowerCase().includes(searchLower))
        && !chunk.expert_name.toLowerCase().includes(searchLower)) return false;
    return true;
  });

  const searchHighlight = new Set(filtered.map(c => c.id));

  // Stats
  const uniqueExperts = new Set(DEMO_CHUNKS.map(c => c.expert_name)).size;
  const uniqueVars = new Set(DEMO_CHUNKS.flatMap(c => c.variable_tags)).size;
  const uniqueSessions = new Set(DEMO_CHUNKS.map(c => c.session_id)).size;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
      {/* Header */}
      <div style={{
        padding: "20px 32px 14px",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        flexShrink: 0,
      }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          Knowledge Base
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "rgba(99,102,241,0.1)", color: "#6366f1" }}>
            Vector DB
          </span>
        </h2>
        <p style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", margin: "3px 0 0" }}>
          Expert interview transcripts chunked, embedded, and stored for retrieval
        </p>
      </div>

      {/* Stats bar */}
      <div style={{ padding: "12px 32px", display: "flex", gap: 10, flexShrink: 0 }}>
        {[
          { label: "Chunks", value: DEMO_CHUNKS.length, color: "#6366f1" },
          { label: "Experts", value: uniqueExperts, color: "#0891b2" },
          { label: "Variables", value: uniqueVars, color: "#10b981" },
          { label: "Sessions", value: uniqueSessions, color: "#f59e0b" },
        ].map(stat => (
          <div key={stat.label} style={{
            flex: 1, padding: "10px 14px", borderRadius: 10,
            border: "1px solid rgba(0,0,0,0.06)", background: "#fff",
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'IBM Plex Mono', monospace", color: stat.color }}>
              {stat.value}
            </div>
            <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.4)", marginTop: 2 }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 32px 40px" }}>
        {/* Ingestion pipeline */}
        <IngestionFlow />

        {/* Embedding map */}
        <EmbeddingMap chunks={DEMO_CHUNKS} searchHighlight={searchHighlight} />

        {/* Search */}
        <div style={{ margin: "14px 0 10px", position: "relative" }}>
          <input
            type="text"
            placeholder="Search knowledge base — e.g. 'domestic violence', 'threshold', 'Kwan'..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: "100%", padding: "10px 14px 10px 36px", borderRadius: 10,
              border: "1.5px solid rgba(0,0,0,0.1)", fontSize: 12,
              outline: "none", background: "#fff", boxSizing: "border-box",
            }}
          />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="2" strokeLinecap="round"
            style={{ position: "absolute", left: 12, top: 12 }}>
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </div>

        {/* Filter pills */}
        <div style={{ display: "flex", gap: 4, marginBottom: 12, flexWrap: "wrap" }}>
          {Object.entries(CHUNK_TYPE_CONFIG).map(([type, cfg]) => (
            <button key={type} onClick={() => setFilterType(filterType === type ? null : type)} style={{
              padding: "3px 10px", borderRadius: 20, border: "none", cursor: "pointer",
              fontSize: 10, fontWeight: 600,
              background: filterType === type ? cfg.color : "rgba(0,0,0,0.04)",
              color: filterType === type ? "#fff" : "rgba(0,0,0,0.5)",
              transition: "all 0.15s ease",
            }}>
              {cfg.label} ({DEMO_CHUNKS.filter(c => c.chunk_type === type).length})
            </button>
          ))}
          <div style={{ width: 1, height: 20, background: "rgba(0,0,0,0.08)", margin: "0 4px", alignSelf: "center" }} />
          {CLUSTER_LABELS.map((label, i) => (
            <button key={i} onClick={() => setFilterCluster(filterCluster === i ? null : i)} style={{
              padding: "3px 10px", borderRadius: 20, border: "none", cursor: "pointer",
              fontSize: 10, fontWeight: 600,
              background: filterCluster === i ? CLUSTER_COLORS[i] : "rgba(0,0,0,0.04)",
              color: filterCluster === i ? "#fff" : "rgba(0,0,0,0.5)",
              transition: "all 0.15s ease",
            }}>
              {label.split(" ")[0]}
            </button>
          ))}
        </div>

        {/* Results count */}
        <div style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", marginBottom: 8 }}>
          Showing {filtered.length} of {DEMO_CHUNKS.length} chunks
          {searchQuery && <span> matching "<strong>{searchQuery}</strong>"</span>}
        </div>

        {/* Chunk cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {filtered.map(chunk => {
            const typeCfg = CHUNK_TYPE_CONFIG[chunk.chunk_type];
            const clusterColor = CLUSTER_COLORS[chunk.embedding_cluster];

            return (
              <div key={chunk.id} style={{
                padding: "12px 16px", borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.06)",
                borderLeft: `3px solid ${clusterColor}`,
                background: "#fff",
              }}>
                {/* Meta row */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{
                    fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
                    background: typeCfg.color, color: "#fff",
                  }}>{typeCfg.icon}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: typeCfg.color }}>{typeCfg.label}</span>
                  <span style={{ fontSize: 10, color: "rgba(0,0,0,0.35)" }}>{chunk.expert_name}</span>
                  <span style={{ fontSize: 9, color: "rgba(0,0,0,0.25)", fontFamily: "'IBM Plex Mono', monospace" }}>{chunk.timestamp}</span>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                    {chunk.variable_tags.map(tag => (
                      <span key={tag} style={{
                        fontSize: 9, padding: "1px 6px", borderRadius: 20,
                        background: "rgba(0,0,0,0.04)", fontWeight: 600,
                        fontFamily: "'IBM Plex Mono', monospace", color: "rgba(0,0,0,0.5)",
                      }}>{tag}</span>
                    ))}
                  </div>
                </div>

                {/* Text */}
                <div style={{ fontSize: 12, lineHeight: 1.6, color: "rgba(0,0,0,0.7)" }}>
                  {searchQuery ? highlightText(chunk.text, searchQuery) : chunk.text}
                </div>

                {/* Bottom bar */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, fontSize: 9, color: "rgba(0,0,0,0.3)" }}>
                  <span>Certainty: <strong style={{ color: chunk.certainty > 0.7 ? "#10b981" : "#f59e0b" }}>{(chunk.certainty * 100).toFixed(0)}%</strong></span>
                  <span>Cluster: <strong style={{ color: clusterColor }}>{CLUSTER_LABELS[chunk.embedding_cluster]}</strong></span>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{chunk.id}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Helper: highlight search matches ── */
function highlightText(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} style={{ background: "rgba(99,102,241,0.2)", borderRadius: 2, padding: "0 2px" }}>{part}</mark>
    ) : part
  );
}
